import type { MusicPort, MusicTrack } from '../types';
import { audioContext, onAudioReady } from './audioContext';

/** Soft lo-fi tempo (audio-list: "nhạc nền êm, không lặp gắt"). */
const BPM = 68;
const BEAT = 60 / BPM;
const LOOKAHEAD_S = 0.4;
const TICK_MS = 120;

const midi = (n: number) => 440 * 2 ** ((n - 69) / 12);
/** Cmaj7 · Am7 · Fmaj7 · G6, one bar each (MIDI notes for the pad). */
const CHORDS: readonly (readonly number[])[] = [
  [48, 52, 55, 59],
  [45, 48, 52, 55],
  [41, 45, 48, 52],
  [43, 47, 50, 52],
];
/** C major pentatonic, two octaves up, for the little melody. */
const MELODY = [72, 74, 76, 79, 81, 84, 86, 88];

/** The player's own tracks sit a little under the sound effects. */
const DEVICE_GAIN = 0.5;

/**
 * Background music (Settings → Nhạc nền), off by default, silent when muted or hidden, quieter during mini
 * games. "Nhạc của game" is made on the fly with WebAudio (no files to download): a warm chord pad, a soft
 * bass and a sparse bell melody from a seeded walk, so it never repeats harshly. "Nhạc trong máy" plays the
 * player's own tracks in a shuffled loop through the same AudioContext, so mute, "Sếp tới!" and a hidden tab
 * silence it the same way.
 */
export function createMusic(): MusicPort {
  let enabled = false;
  let source: 'game' | 'device' = 'game';
  let tracks: readonly MusicTrack[] = [];
  let volume = 0.6;
  let muted = false;
  let ducked = false;
  let paused = false;
  let out: GainNode | undefined;
  let timer: ReturnType<typeof setInterval> | undefined;
  let nextBeat = 0;
  let beat = 0;
  let seed = 7;
  let lastNote = 2;

  const rand = () => {
    seed = (seed * 1664525 + 1013904223) >>> 0;
    return seed / 0x100000000;
  };

  const audible = () => enabled && !muted && !paused;
  const level = () => (audible() && source === 'game' ? volume * (ducked ? 0.35 : 1) * 0.18 : 0);
  const deviceLevel = () =>
    audible() && source === 'device' && tracks.length > 0 ? volume * (ducked ? 0.35 : 1) * DEVICE_GAIN : 0;

  // ---- the player's own tracks ----
  let player: HTMLAudioElement | undefined;
  let playerOut: GainNode | undefined;
  let url: string | undefined;
  let current: string | undefined;
  let queue: string[] = [];
  let failures = 0;

  const playNext = () => {
    if (!player || tracks.length === 0) return;
    if (queue.length === 0) {
      queue = tracks.map((t) => t.id);
      for (let i = queue.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [queue[i], queue[j]] = [queue[j] ?? '', queue[i] ?? ''];
      }
      // Never the same song twice in a row across a reshuffle.
      if (queue.length > 1 && queue[0] === current) queue.push(queue.shift() ?? '');
    }
    const track = tracks.find((t) => t.id === queue[0]);
    queue.shift();
    if (!track) return;
    if (url) URL.revokeObjectURL(url);
    url = URL.createObjectURL(track.blob);
    current = track.id;
    player.src = url;
    void player.play().catch(() => undefined);
  };

  const applyDevice = () => {
    const on = deviceLevel() > 0;
    if (!on && !player) return;
    onAudioReady((ctx) => {
      if (!player) {
        player = new Audio();
        playerOut = ctx.createGain();
        playerOut.gain.value = 0;
        ctx.createMediaElementSource(player).connect(playerOut).connect(ctx.destination);
        player.addEventListener('ended', () => {
          failures = 0;
          playNext();
        });
        // A file the browser cannot play is skipped; after a full round of failures it stops trying.
        player.addEventListener('error', () => {
          if (++failures < tracks.length) playNext();
        });
      }
      const gain = playerOut?.gain;
      gain?.cancelScheduledValues(ctx.currentTime);
      if (deviceLevel() > 0) {
        gain?.setTargetAtTime(deviceLevel(), ctx.currentTime, 0.4);
        if (!current) playNext();
        else if (player.paused) void player.play().catch(() => undefined);
      } else {
        gain?.setValueAtTime(0, ctx.currentTime);
        player.pause();
      }
    });
  };

  const tone = (
    ctx: AudioContext,
    freq: number,
    at: number,
    dur: number,
    gain: number,
    type: OscillatorType,
  ) => {
    if (!out) return;
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = type;
    osc.frequency.value = freq;
    g.gain.setValueAtTime(0, at);
    g.gain.linearRampToValueAtTime(gain, at + Math.min(0.5, dur * 0.3));
    g.gain.exponentialRampToValueAtTime(0.0001, at + dur);
    osc.connect(g).connect(out);
    osc.start(at);
    osc.stop(at + dur + 0.05);
  };

  const scheduleBeat = (ctx: AudioContext, at: number) => {
    const bar = Math.floor(beat / 4) % CHORDS.length;
    const inBar = beat % 4;
    const chord = CHORDS[bar] ?? CHORDS[0] ?? [];
    if (inBar === 0) for (const n of chord) tone(ctx, midi(n), at, BEAT * 4.2, 0.12, 'triangle');
    if (inBar === 0 || inBar === 2) tone(ctx, midi((chord[0] ?? 48) - 12), at, BEAT * 1.8, 0.22, 'sine');
    // A gentle walk over the pentatonic scale: small steps, rests, now and then a pair of notes.
    if (rand() < 0.6) {
      lastNote = Math.max(0, Math.min(MELODY.length - 1, lastNote + Math.round((rand() - 0.5) * 3)));
      tone(ctx, midi(MELODY[lastNote] ?? 76), at, 1.6, 0.09, 'sine');
      if (rand() < 0.25) tone(ctx, midi((MELODY[lastNote] ?? 76) + 12), at + BEAT / 2, 1.2, 0.04, 'sine');
    }
    beat++;
  };

  const tick = () => {
    const ctx = audioContext();
    if (!ctx || !out) return;
    if (nextBeat < ctx.currentTime) nextBeat = ctx.currentTime + 0.05;
    while (nextBeat < ctx.currentTime + LOOKAHEAD_S) {
      scheduleBeat(ctx, nextBeat);
      nextBeat += BEAT;
    }
  };

  const apply = () => {
    const playing = level() > 0;
    onAudioReady((ctx) => {
      if (!out) {
        out = ctx.createGain();
        const warm = ctx.createBiquadFilter();
        warm.type = 'lowpass';
        warm.frequency.value = 2200;
        out.gain.value = 0;
        out.connect(warm).connect(ctx.destination);
      }
      out.gain.cancelScheduledValues(ctx.currentTime);
      // Silence is instant (mute, "Sếp tới!", hidden); only coming back fades in softly.
      if (playing) out.gain.setTargetAtTime(level(), ctx.currentTime, 0.4);
      else out.gain.setValueAtTime(0, ctx.currentTime);
      if (playing && !timer) {
        nextBeat = ctx.currentTime + 0.1;
        timer = setInterval(tick, TICK_MS);
      } else if (!playing && timer) {
        clearInterval(timer);
        timer = undefined;
      }
    });
  };

  const applyAll = () => {
    apply();
    applyDevice();
  };

  return {
    setEnabled(on) {
      enabled = on;
      applyAll();
    },
    setSource(next) {
      source = next;
      applyAll();
    },
    setDeviceTracks(next) {
      tracks = next;
      failures = 0;
      queue = queue.filter((id) => next.some((t) => t.id === id));
      if (current && !next.some((t) => t.id === current)) {
        // The song playing was removed: move on (or stop when none are left).
        current = undefined;
        player?.pause();
      }
      applyDevice();
    },
    setVolume(v) {
      volume = Math.max(0, Math.min(1, v));
      applyAll();
    },
    setMuted(m) {
      muted = m;
      applyAll();
    },
    setDucked(d) {
      ducked = d;
      applyAll();
    },
    setPaused(p) {
      paused = p;
      applyAll();
    },
  };
}
